import type {
  StaffCatalogSearchDebug,
  StaffCatalogSearchHit,
  StaffCatalogSearchResult,
} from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { NameWord } from "../../ui/accessible-name";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { DataTable } from "../../ui/data-table";
import { TableLink } from "../../ui/links";
import { Notice } from "../../ui/notice";
import { ItemStatusBadge } from "../components/ItemStatusBadge";
import {
  bulletList,
  groupHeading,
  hintText,
  inRowTarget,
  panelBox,
  scoreList,
  subheading,
} from "../components/styles";
import { itemTypeLabels } from "../labels";
import { formatDistance } from "../place/distance";
import {
  collapseReasonLabels,
  embeddingStateLabels,
  filterLabel,
  hitReasonLabels,
  listFilters,
  outcomeSummaries,
  reasonSentence,
  SEMANTIC_UNAVAILABLE_NOTICE,
  scoreRows,
  vectorPathLabels,
} from "./labels";
import { EmbeddingBadge, Missing } from "./parts";

const itemPath = (id: string) => `/catalog/items/${encodeURIComponent(id)}`;

/** One sentence for the live region: what the answer is, in words. */
function summaryOf(result: StaffCatalogSearchResult): string {
  if (result.outcome !== "OK") return outcomeSummaries[result.outcome];
  const count = result.hits.length;
  if (count === 0) return "The search ran. No item is listed.";
  const version = result.hits[0]?.rankingVersion;
  return `${count} ${count === 1 ? "item" : "items"} found.${
    version === undefined ? "" : ` Ranking version ${version}.`
  }`;
}

/** The answer of one search. It shows only what the answer carries; it never fills a gap with a guess. */
export function SearchResults({ result }: Readonly<{ result: StaffCatalogSearchResult }>) {
  const noTextMatch = result.reasons.filter((reason) => reason.code === "NO_TEXT_MATCH");
  const problems = result.reasons.filter((reason) => reason.code !== "NO_TEXT_MATCH");
  return (
    <div className="grid min-w-0 gap-3">
      <p role="status" data-search-summary="" className="m-0 mb-2 font-semibold">
        {summaryOf(result)}
      </p>

      {result.semantic === "UNAVAILABLE" ? (
        <Notice tone="warning" data-notice="semantic-unavailable">
          {SEMANTIC_UNAVAILABLE_NOTICE}
        </Notice>
      ) : null}
      {noTextMatch.map((reason) => (
        <Notice tone="info" key={reason.code} data-notice="no-text-match">
          {reasonSentence(reason.code)}
        </Notice>
      ))}
      {result.notApplicable.length > 0 ? (
        <Notice tone="info" data-notice="not-applicable">
          Some of the item types you chose do not use these filters, so the filters did not narrow
          those types: {listFilters(result.notApplicable)}.
        </Notice>
      ) : null}

      {problems.length > 0 ? <ReasonList reasons={problems} kind={result.outcome} /> : null}

      {result.outcome === "NO_MATCH" ? (
        <BlockingFilters filters={result.blockingFilters} debug={result.debug} />
      ) : null}

      {result.outcome === "OK" && result.hits.length > 0 ? <HitTable hits={result.hits} /> : null}

      {result.debug === null ? null : <DebugSummary debug={result.debug} />}
    </div>
  );
}

function ReasonList({
  reasons,
  kind,
}: Readonly<{
  reasons: StaffCatalogSearchResult["reasons"];
  kind: StaffCatalogSearchResult["outcome"];
}>) {
  return (
    <section className={panelBox} aria-labelledby="search-reasons-title">
      <h3 id="search-reasons-title" className={subheading}>
        {kind === "INVALID" ? "What is not valid" : "Why the filters cannot all hold"}
      </h3>
      <ul className={bulletList}>
        {reasons.map((reason) => (
          <li key={`${reason.code}:${reason.filters.join(",")}`}>
            {reasonSentence(reason.code)}
            {reason.filters.length > 0 ? (
              <span className={hintText}> Filters involved: {listFilters(reason.filters)}.</span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function BlockingFilters({
  filters,
  debug,
}: Readonly<{
  filters: StaffCatalogSearchResult["blockingFilters"];
  debug: StaffCatalogSearchDebug | null;
}>) {
  if (filters.length === 0) {
    return (
      <Notice tone="info">
        Taking away any one filter would not help. Change several filters together.
      </Notice>
    );
  }
  return (
    <section className={panelBox} aria-labelledby="search-blocking-title">
      <h3 id="search-blocking-title" className={subheading}>
        Filters that block every item
      </h3>
      <p className="m-0">Taking away any one of these filters would let items through:</p>
      <ul className={bulletList}>
        {filters.map((name) => {
          // The counts exist only when the request asked for debug.
          const count = debug?.blocking.find((entry) => entry.filter === name);
          return (
            <li key={name}>
              {filterLabel(name)}
              {count === undefined ? null : (
                <span className={hintText}>
                  {" "}
                  ({count.itemsWithoutThisFilter}{" "}
                  {count.itemsWithoutThisFilter === 1 ? "item" : "items"} without it)
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const column = createColumnHelper<StaffCatalogSearchHit>();

const detailId = (id: string) => `search-detail-${id}`;
/** The id of the page text that holds the name of a hit (the link in its first cell). The id is the item id: a code, not text. */
const nameId = (id: string) => `search-name-${id}`;

function toggle(
  setOpen: (update: (current: ReadonlySet<string>) => ReadonlySet<string>) => void,
  id: string,
) {
  setOpen((current) => {
    const next = new Set(current);
    if (!next.delete(id)) next.add(id);
    return next;
  });
}

function HitTable({ hits }: Readonly<{ hits: readonly StaffCatalogSearchHit[] }>) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  // The cells read the open set through a ref, so the column list never changes: a new list would remount every cell and drop the focus of
  // the button that was just pressed.
  const openRef = useRef(open);
  openRef.current = open;
  const columns = useMemo(
    () => [
      column.accessor((hit) => hit.item.name, {
        id: "item",
        header: "Item",
        meta: { rowHeader: true },
        cell: (info) => (
          <TableLink
            id={nameId(info.row.original.item.id)}
            to={itemPath(info.row.original.item.id)}
            className="font-serif text-lg font-medium text-foreground"
          >
            {info.getValue()}
          </TableLink>
        ),
      }),
      column.accessor((hit) => itemTypeLabels[hit.item.itemType], { id: "type", header: "Type" }),
      column.accessor(
        (hit) => [hit.item.locality, hit.item.countryCode].filter(Boolean).join(", "),
        {
          id: "locality",
          header: "Locality",
          cell: (info) => info.getValue() || "Not set",
        },
      ),
      column.accessor("status", {
        header: "Status",
        cell: (info) => <ItemStatusBadge status={info.getValue()} />,
      }),
      column.display({
        id: "distance",
        header: "Distance",
        cell: (info) => {
          const { distanceMeters } = info.row.original;
          return distanceMeters === null ? (
            <Missing word="No distance" />
          ) : (
            formatDistance(distanceMeters)
          );
        },
      }),
      column.accessor((hit) => hit.scores.final.toFixed(5), {
        id: "finalScore",
        header: "Final score",
        meta: { className: "tabular-nums" },
      }),
      column.display({
        id: "details",
        header: "Details",
        cell: (info) => {
          const { item } = info.row.original;
          const expanded = openRef.current.has(item.id);
          return (
            <>
              <NameWord id={`${nameId(item.id)}-for`}>for</NameWord>
              {/* The name is "Show details for <the item name>": the page text (the link in the first cell) carries the name, not an attribute. */}
              <Button
                id={`${nameId(item.id)}-toggle`}
                type="button"
                variant="secondary"
                size="sm"
                className={inRowTarget}
                aria-expanded={expanded}
                aria-controls={detailId(item.id)}
                aria-labelledby={`${nameId(item.id)}-toggle ${nameId(item.id)}-for ${nameId(item.id)}`}
                onClick={() => toggle(setOpen, item.id)}
              >
                {expanded ? "Hide details" : "Show details"}
              </Button>
            </>
          );
        },
      }),
    ],
    [],
  );
  return (
    <DataTable
      banded
      columns={columns}
      data={hits}
      getRowId={(hit) => hit.item.id}
      caption="Search results, best first"
      rowClassName={(hit) => (open.has(hit.item.id) ? "bg-accent hover:bg-accent" : undefined)}
      renderRowDetail={(hit) =>
        open.has(hit.item.id)
          ? { id: detailId(hit.item.id), content: <HitDetail hit={hit} /> }
          : null
      }
    />
  );
}

function HitDetail({ hit }: Readonly<{ hit: StaffCatalogSearchHit }>) {
  // The names of the sections are "Scores for <name>" and so on. The name is page text (the link of the row), so each section points at its
  // own heading, a fixed word, and that link: no attribute holds what the person typed.
  const base = `search-detail-${hit.item.id}`;
  const name = nameId(hit.item.id);
  return (
    <div className="grid min-w-0 gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,15rem),1fr))]">
      <NameWord id={`${base}-for`}>for</NameWord>
      <NameWord id={`${base}-why`}>Why</NameWord>
      <NameWord id={`${base}-listed`}>was listed</NameWord>
      <NameWord id={`${base}-of`}>of</NameWord>
      <NameWord id={`${base}-folded`}>Listings folded into</NameWord>
      <section aria-labelledby={`${base}-scores ${base}-for ${name}`} className="col-span-full">
        <h3 id={`${base}-scores`} className={groupHeading}>
          Scores
        </h3>
        <dl className={scoreList}>
          {scoreRows(hit.scores).map((row) => (
            <div key={row.key}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section aria-labelledby={`${base}-why ${name} ${base}-listed`}>
        <h3 className={groupHeading}>Reasons</h3>
        {hit.reasons.length === 0 ? (
          <p className={hintText}>The answer gives no reason.</p>
        ) : (
          <ul className={bulletList}>
            {hit.reasons.map((reason) => (
              <li key={reason}>{hitReasonLabels[reason]}</li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby={`${base}-vector ${base}-of ${name}`}>
        <h3 id={`${base}-vector`} className={groupHeading}>
          Meaning vector
        </h3>
        <p className="m-0 flex flex-wrap items-center gap-2">
          <EmbeddingBadge state={hit.embedding.state} />
          <span className={hintText}>{embeddingStateLabels[hit.embedding.state].help}</span>
        </p>
        <p className={hintText}>
          Model: {hit.embedding.modelKey === null ? "none" : hit.embedding.modelKey}
        </p>
      </section>
      {hit.collapsed.length > 0 ? (
        <section aria-labelledby={`${base}-folded ${name}`}>
          <h3 className={groupHeading}>Near duplicates folded into this item</h3>
          <ul className={bulletList}>
            {hit.collapsed.map((entry) => (
              <li key={entry.id}>
                <Link className="text-link underline underline-offset-4" to={itemPath(entry.id)}>
                  {entry.name}
                </Link>{" "}
                <span className={hintText}>({collapseReasonLabels[entry.reason]})</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <p className={cn(hintText, "col-span-full")}>Ranking version {hit.rankingVersion}.</p>
    </div>
  );
}

function DebugSummary({ debug }: Readonly<{ debug: StaffCatalogSearchDebug }>) {
  const states = Object.entries(debug.embeddingStates) as [
    keyof StaffCatalogSearchDebug["embeddingStates"],
    number,
  ][];
  return (
    <section className={panelBox} aria-labelledby="search-debug-title">
      <h3 id="search-debug-title" className={subheading}>
        Debug summary
      </h3>
      <dl className={scoreList}>
        <div>
          <dt>Items that pass every filter</dt>
          <dd>{debug.eligibleCount}</dd>
        </div>
        <div>
          <dt>Candidates by name</dt>
          <dd>{debug.nameCandidates}</dd>
        </div>
        <div>
          <dt>Candidates by words</dt>
          <dd>{debug.lexicalCandidates}</dd>
        </div>
        <div>
          <dt>Candidates by meaning</dt>
          <dd>{debug.vectorCandidates}</dd>
        </div>
        <div>
          <dt>Candidates by distance</dt>
          <dd>{debug.geoCandidates}</dd>
        </div>
        <div>
          <dt>Meaning search path</dt>
          <dd>{vectorPathLabels[debug.vectorPath]}</dd>
        </div>
        <div>
          <dt>Embedding model</dt>
          <dd>{debug.embeddingModelKey ?? "None"}</dd>
        </div>
      </dl>
      <h4 className={groupHeading}>Meaning vectors of the items that pass every filter</h4>
      <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-2 p-0">
        {states.map(([state, count]) => (
          <li key={state} className="flex items-center gap-1.5">
            <EmbeddingBadge state={state} /> <span>{count}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
